import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import './Dashboard.css';
import Pagination from '../../components/Pagination';
import Header from './Header';
import { CATEGORIES } from '../../constants/categories';
import AdmissionForm from './AdmissionForm';
import ICHarmonyAdmin from './ICHarmonyAdmin';
import { ClimbingBoxLoader } from 'react-spinners';

const POSTS_PER_PAGE = 10;

const Dashboard = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('posts');

  useEffect(() => {
    checkUser();
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) navigate('/admin/login');
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);
      
      // First, get the total count of posts
      const { count: totalCount, error: countError } = await supabase
        .from('blog_posts')
        .select('*', { count: 'exact', head: true });
      
      if (countError) throw countError;
      setTotalPosts(totalCount);
      
      // Calculate pagination range
      const from = (currentPage - 1) * POSTS_PER_PAGE; 
      const to = from + POSTS_PER_PAGE - 1;

      // Fetch paginated posts ordered by created_at in descending order (newest first)
      const { data, error } = await supabase
        .from('blog_posts')
        .select('id, title, slug, excerpt, content, image_url, author, status, published_at, view_count, created_at, updated_at, category')
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;
      setPosts(data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus postingan ini?')) {
      try {
        const { error } = await supabase
          .from('blog_posts')
          .delete()
          .eq('id', id);
        
        if (error) throw error;
        setPosts(posts.filter(post => post.id !== id));
        
        // Refresh the posts count after deletion
        const { count, error: countError } = await supabase
          .from('blog_posts')
          .select('*', { count: 'exact', head: true });
          
        if (!countError) {
          setTotalPosts(count);
          
          // If we deleted the last item on the current page, go back one page
          const totalPages = Math.ceil(count / POSTS_PER_PAGE);
          if (currentPage > totalPages && currentPage > 1) {
            setCurrentPage(currentPage - 1);
          }
        }
      } catch (error) {
        console.error('Error deleting post:', error);
      }
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  const handleCreatePost = () => {
    navigate('/admin/posts/new');
  };

  const handleEditPost = (postId) => {
    navigate(`/admin/posts/edit/${postId}`);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  return (
    <div>
      <Header 
        title="Dasbor"
        onCreatePost={handleCreatePost}
        onLogout={handleLogout}
      />
        <div className="dashboard-container">
          <div className="tab-bar">
            <button
              className={`tab-button ${activeTab === 'posts' ? 'active' : ''}`}
              onClick={() => setActiveTab('posts')}
            >
              Postingan Blog
            </button>
            <button
              className={`tab-button ${activeTab === 'admission' ? 'active' : ''}`}
              onClick={() => setActiveTab('admission')}
            >
              Informasi PSB
            </button>
            <button
              className={`tab-button ${activeTab === 'ic-harmony' ? 'active' : ''}`}
              onClick={() => setActiveTab('ic-harmony')}
            >
              IC Harmony
            </button>
          </div>
          <div className="content-container">
            {activeTab === 'posts' && (
              <div>
                {loading ? ( 
                  <div className="loading">
                    <ClimbingBoxLoader color='var(--primary-color)' size={10} />
                  </div> 
                ) : posts.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">📝</div>
                    <p>Tidak ada postingan ditemukan</p>
                    <button onClick={handleCreatePost} className="create-button">Buat postingan pertama Anda</button>
                  </div>
                ) : (
                  <div className="posts-grid">
                    {posts.map((post) => (
                      <div key={post.id} className="post-card">
                        <div className="post-card-header">
                          <div className="post-badges">
                            <span className={`status-badge ${post.status.toLowerCase()}`}>
                              {post.status}
                            </span>
                            <span className="category-label" style={{ backgroundColor: CATEGORIES[post.category.toUpperCase()]?.backgroundColor, color: CATEGORIES[post.category.toUpperCase()]?.color }}>
                              {CATEGORIES[post.category.toUpperCase()]?.label}
                            </span>
                          </div>
                          <span className="view-count-badge">
                            👁 {(post.view_count || 0).toLocaleString('id-ID')}
                          </span>
                        </div>
                        
                        <div className="post-card-body">
                          <h3 className="post-card-title">{post.title}</h3>
                          <p className="post-card-excerpt">{post.excerpt.substring(0, 100)}...</p>
                        </div>
                        
                        <div className="post-card-footer">
                          <div className="post-meta">
                            <span className="post-author">✍️ {post.author || 'Anonim'}</span>
                            <span className="post-date">
                              📅 {new Date(post.created_at).toLocaleDateString('id-ID', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </span>
                          </div>
                          <div className="post-card-actions">
                            <button
                              onClick={() => handleEditPost(post.id)}
                              className="action-button edit-button"
                              title="Edit postingan"
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => handleDelete(post.id)}
                              className="action-button delete-button"
                              title="Hapus postingan"
                            >
                              🗑️ Hapus
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {posts.length > 0 && totalPosts > POSTS_PER_PAGE && (
                  <Pagination
                    currentPage={currentPage}
                    totalItems={totalPosts}
                    itemsPerPage={POSTS_PER_PAGE}
                    onPageChange={handlePageChange}
                  />
                )}
              </div>
            )}
            {activeTab === 'admission' && <AdmissionForm />}
            {activeTab === 'ic-harmony' && <ICHarmonyAdmin />}
          </div>
        </div>
    </div>
  );
};

export default Dashboard;
